import { useState, useRef, useCallback } from "react";

export const useScrollDetection = <T extends HTMLElement>() => {
  const [scrolled, setScrolled] = useState(false);
  const elementRef = useRef<T | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const onScroll = useCallback(() => {
    if (elementRef.current) {
      setScrolled(elementRef.current.scrollTop > 0);
    }
  }, []);

  // Use a callback ref to attach listener when element becomes available
  const scrollElementRef = useCallback(
    (element: T | null) => {
      // Clean up previous listener
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Update ref
      elementRef.current = element;

      if (element) {
        // Create new AbortController for this element
        const controller = new AbortController();
        abortControllerRef.current = controller;

        // Attach listener with modern AbortController pattern
        element.addEventListener("scroll", onScroll, {
          signal: controller.signal,
          passive: true,
        });

        // Check initial scroll state
        setScrolled(element.scrollTop > 0);
      } else {
        setScrolled(false);
      }
    },
    [onScroll],
  );

  return { scrolled, scrollElementRef };
};
